import { useTranslation } from 'next-i18next';
import { memo } from 'react';
import SidebarLanguageDropdown from '../SidebarLanguageDropdown/SidebarLanguageDropdown';
import { VisitStandardWebsiteButton } from '../VisitStandardWebsiteButton';
import Button from '../Button';
import Booking from '../Icons/Booking';
import Coupon from '../Icons/Coupon';
import Setting from '../Icons/Setting';
import LoadingBox from '../LoadingBox';
import { useLogout } from '../../../core/account/AuthServiceHooks';
import SkipTranslationWrapper from '../SkipTranslationWrapper';
import UserProfile from '../Icons/UserProfile';
import { useSelector } from '../../../core/redux/store';
import MobileSidebarLayout from '../MobileSidebarLayout/MobileSidebarLayout';

type Props = {
  onShowSidebar: (value: boolean) => void;
  showBookingConfirmation?: boolean;
  showSidebar?: boolean;
};

const Sidebar = ({ showBookingConfirmation = true, showSidebar, onShowSidebar }: Props) => {
  const { t } = useTranslation(['component']);
  const {
    family_name: familyName,
    given_name: givenName,
    accountId,
  } = useSelector((state) => state.account.userAttributes);

  const isLoggedIn = Boolean(accountId);
  const [logout, logoutLoading] = useLogout();

  const events = {
    onClose: () => {
      onShowSidebar(false);
    },
  };

  const menu = [
    {
      id: 'my-trips',
      icon: <Booking size={24} color="gray" />,
      name: t('sidebar.menu.my_trips'),
    },
    {
      id: 'my-coupons',
      icon: <Coupon size={24} color="gray" />,
      name: t('sidebar.menu.my_coupons'),
    },
    {
      id: 'details',
      icon: <Setting size={24} color="gray" />,
      name: t('sidebar.menu.account'),
    },
  ];

  return (
    <MobileSidebarLayout visible={Boolean(showSidebar)} onClose={events.onClose}>
      <section className="flex flex-col gap-4">
        {!isLoggedIn ? (
          <Button color="green" textColor="white" rounded className="w-full" to="/account/login">
            {t('component:sidebar.button.login_or_register')}
          </Button>
        ) : (
          <div className="flex h-18 items-center gap-2 rounded-2xl bg-red-gradient p-4 text-white">
            <UserProfile size={40} />
            <SkipTranslationWrapper tag="p" className="w-full truncate">
              {familyName} {givenName}
            </SkipTranslationWrapper>
          </div>
        )}
      </section>
      <section className="mx-2 flex flex-col gap-4 pt-4">
        <SidebarLanguageDropdown />
      </section>
      {isLoggedIn && (
        <section className="mx-2 flex flex-col">
          {menu.map((object) => (
            <Button
              to={`/account/${object?.id}`}
              key={object?.id}
              color="transparent"
              textColor="gray"
              fontWeight="normal"
              size="medium"
              className="flex w-full !justify-start gap-4 px-0"
            >
              {object?.icon}
              {object?.name}
            </Button>
          ))}
        </section>
      )}
      <section className="mx-2 flex flex-col gap-4 pt-4">
        <VisitStandardWebsiteButton size="medium" />
        {showBookingConfirmation && !isLoggedIn && (
          <Button
            color="transparent"
            textColor="gray"
            borderColor="gray"
            border
            rounded
            to="/guest/booking-id"
          >
            {t('component:sidebar.menu.booking_confirmation')}
          </Button>
        )}
        {isLoggedIn && (
          <Button
            color="transparent"
            textColor="gray"
            borderColor="gray"
            border
            rounded
            onClick={logout}
          >
            {t('component:sidebar.button.logout')}
          </Button>
        )}
        {logoutLoading && <LoadingBox domParent={document.getElementById('app')} />}
      </section>
    </MobileSidebarLayout>
  );
};

export default memo(Sidebar);
